<template>
  <div class="absolute z-50 top-0 left-0 p-6 flex gap-3">
    <button @click="$emit('goBack')" class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300">
      <img class="h-4 w-4 brightness-200" src="../../assets/icons/leftarrow.svg" />
    </button>
    <Popover as="div" class="relative inline-block text-left">
      <div>
        <PopoverButton
          class="group flex justify-center items-center rounded-md bg-gray-800 duration-200 w-8 h-8 text-left text-sm font-medium text-text-primary hover:bg-gray-700 focus:outline-none focus:ring-0"
        >
          <img class="h-4" src="../../assets/icons/settings.svg">
        </PopoverButton>
      </div>
      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <PopoverPanel
          class="absolute w-[240px] p-4 mt-2 origin-top rounded-md border border-gray-700 shadow-lg outline-none bg-gray-950"
        >
          <div class="flex flex-col justify-center mb-4">
            <div class="flex gap-x-2 items-center pb-2 text-text-secondary uppercase text-xs tracking-[1.5px]">
              {{ $t('viewer.controls.timeline_filters') }}
            </div>
            <div class="flex py-2 justify-center items-center">
              <h2 class="text-sm text-text-primary flex-1">{{ $t('viewer.controls.show_round_info') }}</h2>
              <Switch
                :model-value="showRoundInfo"
                @update:model-value="$emit('update:showRoundInfo', $event)"
                :class="showRoundInfo ? 'bg-primary-500' : 'bg-gray-600'"
                class="relative inline-flex h-3 w-5 items-center rounded-full"
              >
                <span
                  :class="showRoundInfo ? 'translate-x-[9px] bg-primary-50' : 'translate-x-[1px] bg-gray-800'"
                  class="inline-block h-2.5 w-2.5 transform rounded-full transition"
                />
              </Switch>
            </div>

            <div class="flex justify-center items-center">
              <h2 class="text-sm text-text-primary flex-1">{{ $t('viewer.controls.show_grenade_info') }}</h2>
              <Switch
                :model-value="showGrenadeGroups"
                @update:model-value="$emit('update:showGrenadeGroups', $event)"
                :class="showGrenadeGroups ? 'bg-primary-500' : 'bg-gray-600'"
                class="relative inline-flex h-3 w-5 items-center rounded-full"
              >
                <span
                  :class="showGrenadeGroups ? 'translate-x-[9px] bg-primary-50' : 'translate-x-[1px] bg-gray-800'"
                  class="inline-block h-2.5 w-2.5 transform rounded-full transition"
                />
              </Switch>
            </div>
          </div>
          <div class="flex flex-col gap-2">
            <div class="flex gap-x-2 items-center pb-2 text-text-secondary uppercase text-xs tracking-[1.5px]">
              {{ $t('viewer.controls.key_binds') }}
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_play_pause') }}</h1>
              <h2 class="text-text-secondary text-sm">{{ $t('viewer.controls.key_space') }}</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_next_round') }}</h1>
              <h2 class="text-text-secondary text-sm">N</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_previous_round') }}</h1>
              <h2 class="text-text-secondary text-sm">P</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_forward') }}</h1>
              <h2 class="text-text-secondary text-sm"><img src="../../assets/icons/rightarrow.svg"></h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_back') }}</h1>
              <h2 class="text-text-secondary text-sm"><img src="../../assets/icons/leftarrow.svg"></h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_next_frame') }}</h1>
              <h2 class="text-text-secondary text-sm">.</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_previous_frame') }}</h1>
              <h2 class="text-text-secondary text-sm">,</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_fast_forward') }}</h1>
              <h2 class="text-text-secondary text-sm">F</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_slow_down') }}</h1>
              <h2 class="text-text-secondary text-sm">S</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.keybind_toggle_map') }}</h1>
              <h2 class="text-text-secondary text-sm">M</h2>
            </div>
            <div class="flex font-light">
              <h1 class="text-text-primary text-sm flex-1">{{ $t('viewer.controls.screenshot') }}</h1>
              <h2 class="text-text-secondary text-sm">C</h2>
            </div>
          </div>

        </PopoverPanel>
      </transition>
    </Popover>
    <div v-if="showModeSwitch" class="flex rounded-md bg-gray-800 overflow-hidden h-8 text-xs text-text-primary">
      <button
        v-for="mode in ['2d', '3d']"
        :key="mode"
        class="px-2 focus:outline-none"
        :class="viewerMode === mode ? 'bg-primary-600' : 'hover:bg-gray-700'"
        @click="$emit('update:viewerMode', mode)"
      >{{ mode.toUpperCase() }}</button>
    </div>
    <button
      v-if="viewerMode !== '3d'"
      @click="$emit('toggleDragHand')"
      :class="dragHandActive ? 'bg-primary-600' : 'bg-gray-800'"
      class="flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
      :title="$t('viewer.controls.drag_hand')"
    >
      <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
      </svg>
    </button>
    <button
      v-if="viewerMode !== '3d'"
      @click="$emit('togglePenTool')"
      :class="penToolActive ? 'bg-primary-600' : 'bg-gray-800'"
      class="flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
      :title="$t('viewer.controls.pen_tool')"
    >
      <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
      </svg>
    </button>
    <div class="relative">
      <button
        @click="captureMenuOpen = !captureMenuOpen"
        :disabled="captureDisabled"
        class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none
          hover:scale-110 duration-300 disabled:opacity-50 disabled:hover:scale-100"
        :title="$t('viewer.controls.screenshot')"
      >
        <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0
              011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0
              01-2-2V9z M15 13a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      </button>
      <div
        v-if="captureMenuOpen && !captureDisabled"
        class="absolute mt-2 w-40 py-1 rounded-md border border-gray-700 shadow-lg bg-gray-950"
      >
        <button
          class="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-gray-800"
          @click="capture('download')"
        >{{ $t('viewer.controls.download_png') }}</button>
        <button
          v-if="canCopy"
          class="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-gray-800"
          @click="capture('clipboard')"
        >{{ $t('viewer.controls.copy_image') }}</button>
        <button
          class="block w-full px-3 py-2 text-left text-sm text-text-primary hover:bg-gray-800"
          @click="captureMenuOpen = false; $emit('exportGif')"
        >{{ $t('viewer.controls.export_gif_menu') }}</button>
      </div>
    </div>
    <button
      v-if="drawnPathsLength > 0"
      @click="$emit('clearDrawings')"
      class="bg-gray-800 flex items-center justify-center rounded-md w-8 h-8 focus:outline-none hover:scale-110 duration-300"
      :title="$t('viewer.controls.clear_drawings')"
    >
      <svg class="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    </button>
  </div>
</template>

<script setup>
import {
  Popover, PopoverButton, PopoverPanel, Switch,
} from '@headlessui/vue';
import { ref } from 'vue';

const props = defineProps({
  dragHandActive: Boolean,
  penToolActive: Boolean,
  drawnPathsLength: Number,
  showRoundInfo: Boolean,
  showGrenadeGroups: Boolean,
  captureDisabled: Boolean,
  canCopy: Boolean,
  showModeSwitch: Boolean,
  viewerMode: { type: String, default: '2d' },
});

const emit = defineEmits([
  'goBack',
  'toggleDragHand',
  'togglePenTool',
  'clearDrawings',
  'update:showRoundInfo',
  'update:showGrenadeGroups',
  'screenshot',
  'exportGif',
  'update:viewerMode',
]);

const captureMenuOpen = ref(false);

function capture(method) {
  captureMenuOpen.value = false;
  emit('screenshot', method);
}
</script>
